import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { tasksService } from '../../../../services/tasks';
import { mapTaskIdToKey } from '../../../../utils/taskKey';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('AdvanceDirectiveExplain');

export const AdvanceDirectiveExplainScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();

  const handleDone = async () => {
    try {
      const taskKey = mapTaskIdToKey(56); // Advance Directive task ID
      await tasksService.completeTask(taskKey);
      // Mark decline flag so safeguards keep it completed without data
      const AsyncStorage = require('@react-native-async-storage/async-storage').default;
      await AsyncStorage.setItem('task_declined_56', 'true');
    } catch (e) {
      // Non-blocking; still return to checklist
      logger.debug('Error completing advance directive task on decline:', e);
    }
    // Navigate explicitly to the FinalCare stack's checklist to trigger focus/refresh
    (navigation as any).navigate('HealthcareDirectives', { screen: 'HealthcareDirectivesChecklist', params: { refreshKey: Date.now() } });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Advance Directive Information</Text>
      </View>

      <View style={styles.contentContainer}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>What is an Advance Directive?</Text>
            <Text style={styles.text}>
              An Advance Directive is a legal document that allows you to state your preferences for medical treatment in case you become unable to make decisions due to illness, injury, or incapacity.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Why is it Important?</Text>
            <Text style={styles.bulletPoint}>• Preserves autonomy – Ensures your healthcare choices are respected</Text>
            <Text style={styles.bulletPoint}>• Reduces family burden – Prevents conflict when loved ones must make difficult decisions</Text>
            <Text style={styles.bulletPoint}>• Guides physicians – Gives doctors clear instructions about your preferences</Text>
            <Text style={styles.bulletPoint}>• Legally binding – Carries legal weight when properly executed</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Main Types of Advance Directives</Text>

            <Text style={styles.subTitle}>1. Living Will</Text>
            <Text style={styles.text}>Specifies what types of medical care you do or do not want, including:</Text>
            <Text style={styles.bulletPoint}>• Resuscitation (CPR)</Text>
            <Text style={styles.bulletPoint}>• Mechanical ventilation</Text>
            <Text style={styles.bulletPoint}>• Tube feeding</Text>
            <Text style={styles.bulletPoint}>• Dialysis</Text>
            <Text style={styles.bulletPoint}>• Comfort care</Text>
            <Text style={styles.bulletPoint}>• Organ/tissue donation</Text>

            <Text style={styles.subTitle}>2. Medical Power of Attorney</Text>
            <Text style={styles.text}>Appoints a healthcare proxy to make medical decisions on your behalf when you're unable to do so.</Text>

            <Text style={styles.subTitle}>3. Do Not Resuscitate (DNR) Orders</Text>
            <Text style={styles.text}>A medical order indicating no CPR should be performed if breathing or heartbeat stops.</Text>

            <Text style={styles.subTitle}>4. POLST/MOLST</Text>
            <Text style={styles.text}>Physician Orders for Life-Sustaining Treatment - used for patients with serious illness, containing immediate medical orders for emergency personnel.</Text>
          </View>

          <TouchableOpacity style={styles.doneButton} onPress={handleDone}>
            <Text style={styles.doneButtonText}>Done</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(100),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(20),
  },
  section: {
    marginBottom: ms(24),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  subTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginTop: ms(16),
    marginBottom: ms(8),
  },
  text: {
    fontSize: ms(16, 0.3),
    color: '#4A4A4A',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(8),
  },
  bulletPoint: {
    fontSize: ms(16, 0.3),
    color: '#4A4A4A',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(8),
    paddingLeft: ms(8),
  },
  bottomPadding: {
    height: s(100),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    marginHorizontal: ms(20),
    marginTop: ms(30),
    marginBottom: ms(20),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  doneButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
});

export default AdvanceDirectiveExplainScreen;